import { createLowlight, all } from 'lowlight';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var NewLineSymbol;
(function (NewLineSymbol) {
    NewLineSymbol[NewLineSymbol["CRLF"] = 1] = "CRLF";
    NewLineSymbol[NewLineSymbol["CR"] = 2] = "CR";
    NewLineSymbol[NewLineSymbol["LF"] = 3] = "LF";
    NewLineSymbol[NewLineSymbol["NEWLINE"] = 4] = "NEWLINE";
    NewLineSymbol[NewLineSymbol["NORMAL"] = 5] = "NORMAL";
    NewLineSymbol[NewLineSymbol["NULL"] = 6] = "NULL";
})(NewLineSymbol || (NewLineSymbol = {}));
var DiffModeEnum;
(function (DiffModeEnum) {
    // github like
    DiffModeEnum[DiffModeEnum["SplitGitHub"] = 1] = "SplitGitHub";
    // gitlab like
    DiffModeEnum[DiffModeEnum["SplitGitLab"] = 2] = "SplitGitLab";
    DiffModeEnum[DiffModeEnum["Split"] = 3] = "Split";
    DiffModeEnum[DiffModeEnum["Unified"] = 4] = "Unified";
})(DiffModeEnum || (DiffModeEnum = {}));

const processAST = (ast) => {
    let lineNumber = 1;
    const syntaxObj = {};
    const loopAST = (nodes, wrapper) => {
        nodes.forEach((node) => {
            if (node.type === "text") {
                if (node.value.indexOf("\n") === -1) {
                    const valueLength = node.value.length;
                    if (!syntaxObj[lineNumber]) {
                        node.startIndex = 0;
                        node.endIndex = valueLength - 1;
                        const item = {
                            value: node.value,
                            lineNumber,
                            valueLength,
                            nodeList: [{ node, wrapper }],
                        };
                        syntaxObj[lineNumber] = item;
                    }
                    else {
                        node.startIndex = syntaxObj[lineNumber].valueLength;
                        node.endIndex = node.startIndex + valueLength - 1;
                        syntaxObj[lineNumber].value += node.value;
                        syntaxObj[lineNumber].valueLength += valueLength;
                        syntaxObj[lineNumber].nodeList.push({ node, wrapper });
                    }
                    node.lineNumber = lineNumber;
                    return;
                }
                const lines = node.value.split("\n");
                node.children = node.children || [];
                for (let i = 0; i < lines.length; i++) {
                    const _value = i === lines.length - 1 ? lines[i] : lines[i] + "\n";
                    const _lineNumber = i === 0 ? lineNumber : ++lineNumber;
                    const _valueLength = _value.length;
                    const _node = {
                        type: "text",
                        value: _value,
                        startIndex: Infinity,
                        endIndex: Infinity,
                        lineNumber: _lineNumber,
                    };
                    if (!syntaxObj[_lineNumber]) {
                        _node.startIndex = 0;
                        _node.endIndex = _valueLength - 1;
                        const item = {
                            value: _value,
                            lineNumber: _lineNumber,
                            valueLength: _valueLength,
                            nodeList: [{ node: _node, wrapper }],
                        };
                        syntaxObj[_lineNumber] = item;
                    }
                    else {
                        _node.startIndex = syntaxObj[_lineNumber].valueLength;
                        _node.endIndex = _node.startIndex + _valueLength - 1;
                        syntaxObj[_lineNumber].value += _value;
                        syntaxObj[_lineNumber].valueLength += _valueLength;
                        syntaxObj[_lineNumber].nodeList.push({ node: _node, wrapper });
                    }
                    node.children.push(_node);
                }
                node.lineNumber = lineNumber;
                return;
            }
            if (node.children) {
                loopAST(node.children, node);
                node.lineNumber = lineNumber;
            }
        });
    };
    loopAST(ast.children);
    return { syntaxFileObject: syntaxObj, syntaxFileLineNumber: lineNumber };
};

function _getAST(_raw, _fileName, _lang, _theme) {
    if (process.env.NODE_ENV === "development") {
        console.warn("[@git-diff-view/lowlight] `_getAST` is a type-level helper and is not meant to be called at runtime.");
    }
    return {};
}

const lowlight = createLowlight(all);
// !SEE https://github.com/highlightjs/highlightjs-vue
lowlight.register("vue", function hljsDefineVue(hljs) {
    return {
        subLanguage: "xml",
        contains: [
            hljs.COMMENT("<!--", "-->", {
                relevance: 10,
            }),
            {
                begin: /^(\s*)(<script>)/gm,
                end: /^(\s*)(<\/script>)/gm,
                subLanguage: "javascript",
                excludeBegin: true,
                excludeEnd: true,
            },
            {
                begin: /^(?:\s*)(?:<script\s+lang=(["'])ts\1>)/gm,
                end: /^(\s*)(<\/script>)/gm,
                subLanguage: "typescript",
                excludeBegin: true,
                excludeEnd: true,
            },
            {
                begin: /^(\s*)(<style(\s+scoped)?>)/gm,
                end: /^(\s*)(<\/style>)/gm,
                subLanguage: "css",
                excludeBegin: true,
                excludeEnd: true,
            },
            {
                begin: /^(?:\s*)(?:<style(?:\s+scoped)?\s+lang=(["'])(?:s[ca]ss)\1(?:\s+scoped)?>)/gm,
                end: /^(\s*)(<\/style>)/gm,
                subLanguage: "scss",
                excludeBegin: true,
                excludeEnd: true,
            },
            {
                begin: /^(?:\s*)(?:<style(?:\s+scoped)?\s+lang=(["'])stylus\1(?:\s+scoped)?>)/gm,
                end: /^(\s*)(<\/style>)/gm,
                subLanguage: "stylus",
                excludeBegin: true,
                excludeEnd: true,
            },
        ],
    };
});
const instance = { name: "lowlight" };
let _maxLineToIgnoreSyntax = 2000;
const _ignoreSyntaxHighlightList = [];
Object.defineProperty(instance, "maxLineToIgnoreSyntax", {
    get: () => _maxLineToIgnoreSyntax,
});
Object.defineProperty(instance, "setMaxLineToIgnoreSyntax", {
    value: (v) => {
        _maxLineToIgnoreSyntax = v;
    },
});
Object.defineProperty(instance, "ignoreSyntaxHighlightList", {
    get: () => _ignoreSyntaxHighlightList,
});
Object.defineProperty(instance, "setIgnoreSyntaxHighlightList", {
    value: (v) => {
        _ignoreSyntaxHighlightList.length = 0;
        _ignoreSyntaxHighlightList.push(...v);
    },
});
Object.defineProperty(instance, "getAST", {
    value: (raw, fileName, lang) => {
        let hasRegisteredLang = true;
        if (!lowlight.registered(lang || "")) {
            if (process.env.NODE_ENV === "development") {
                console.warn(`[@git-diff-view/lowlight] Unsupported language: "${lang}".`);
            }
            hasRegisteredLang = false;
        }
        if (fileName &&
            highlighter.ignoreSyntaxHighlightList.some((item) => item instanceof RegExp ? item.test(fileName) : fileName === item)) {
            if (process.env.NODE_ENV === "development") {
                console.warn(`[@git-diff-view/lowlight] Skipping syntax highlighting for "${fileName}" because it is listed in ignoreSyntaxHighlightList.`);
            }
            return;
        }
        if (hasRegisteredLang && lang) {
            return lowlight.highlight(lang, raw);
        }
        else {
            return lowlight.highlightAuto(raw);
        }
    },
});
Object.defineProperty(instance, "processAST", {
    value: (ast) => {
        return processAST(ast);
    },
});
Object.defineProperty(instance, "hasRegisteredCurrentLang", {
    value: (lang) => {
        return lowlight.registered(lang);
    },
});
Object.defineProperty(instance, "getHighlighterEngine", {
    value: () => lowlight,
});
Object.defineProperty(instance, "type", { value: "class" });
const versions = "0.1.8";
const highlighter = instance;

export { _getAST, highlighter, processAST, versions };
//# sourceMappingURL=index.mjs.map
